import { useEffect, useId, useRef, useState } from "react";
import axios from "axios";
import { postCashbackRead } from "./cashbackRead";
import { Info } from "lucide-react";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CASHBACK_MAINTENANCE_MESSAGE } from "./cashbackAvailability";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/alloyal/cashback`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("auth_token")}` } });
const money = (value) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(value));
const statuses = { pending: "Pendente", approved: "Aprovado", available: "Disponível", transferred: "Transferido", in_transfer: "Em transferência", cancelled: "Cancelado", canceled: "Cancelado", rejected: "Não aprovado" };
const recordDate = (value) => {
  if (!value) return "";
  if (/^\d{2}\/\d{2}\/\d{4}/.test(value)) return value;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString("pt-BR");
};

const balanceHelp = {
  available_amount: "Cashback liberado para solicitar resgate, conforme as condições da Alloyal. O resgate por Pix na plataforma ainda está em atualização.",
  receivable_amount: "Soma do cashback pendente e aprovado. Esse valor ainda não está disponível para resgate e depende da liberação pela Alloyal.",
  in_transfer_amount: "Cashback de um resgate já solicitado, que está sendo processado para transferência.",
  transferred_amount: "Total de cashback que já foi transferido para você em resgates anteriores.",
};

function BalanceHelp({ label, balanceKey, withdrawalEnabled }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const pinned = useRef(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const show = () => { clearTimeout(timer.current); setOpen(true); };
  const leave = () => {
    clearTimeout(timer.current);
    if (!pinned.current) timer.current = setTimeout(() => setOpen(false), 180);
  };
  const description = balanceKey === "available_amount" && withdrawalEnabled
    ? "Cashback liberado para solicitar resgate, conforme as condições da Alloyal."
    : balanceHelp[balanceKey];
  return <Popover open={open} onOpenChange={(value) => { clearTimeout(timer.current); pinned.current = false; setOpen(value); }}>
    <PopoverAnchor asChild>
      <button type="button" aria-label={`Entenda o saldo ${label.toLowerCase()}`} aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? id : undefined}
        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-zinc-400 hover:bg-white/10 hover:cashback-accent-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-300"
        onPointerEnter={(event) => { if (event.pointerType === "mouse") show(); }} onPointerLeave={leave}
        onFocus={show} onBlur={leave}
        onClick={() => { clearTimeout(timer.current); pinned.current = !pinned.current; setOpen(pinned.current); }}>
        <Info aria-hidden="true" className="h-4 w-4" />
      </button>
    </PopoverAnchor>
    <PopoverContent id={id} aria-label={label} side="bottom" sideOffset={8} collisionPadding={16}
      className="max-w-[calc(100vw-2rem)] border-violet-300/20 bg-zinc-900 text-sm leading-relaxed text-zinc-200 shadow-xl"
      onOpenAutoFocus={(event) => event.preventDefault()} onCloseAutoFocus={(event) => event.preventDefault()}
      onPointerEnter={() => clearTimeout(timer.current)} onPointerLeave={leave}>
      <p className="mb-1 font-semibold cashback-accent-text">{label}</p><p>{description}</p>
    </PopoverContent>
  </Popover>;
}

export default function CashbackWallet({ memberId, onError, onNeedsCpf, compact = false, onLoadState, initialData, onOrders }) {
  const historyId = useId();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [wallet, setWallet] = useState(initialData?.wallet || null);
  const [state, setState] = useState(initialData ? initialData.wallet ? "ready" : "failed" : "loading");
  useEffect(() => { onLoadState?.(state === "ready" ? "ready" : state === "loading" ? "loading" : "failed"); }, [state, onLoadState]);
  const [retry, setRetry] = useState(0);
  const [confirmation, setConfirmation] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState("");
  const submitLock = useRef(false);
  const mounted = useRef(true);
  const handlers = useRef({ onError, onNeedsCpf });
  handlers.current = { onError, onNeedsCpf };
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    if (initialData && retry === 0) return;
    const controller = new AbortController();
    setState("loading");
    setWallet(null);
    axios.get(`${API}/status`, { ...auth(), signal: controller.signal, timeout: 45000 })
      .then(async ({ data: status }) => {
        if (controller.signal.aborted) return;
        if (!status.enabled) { setState("unavailable"); return; }
        if (!status.eligible) { setState("ineligible"); return; }
        if (status.needs_cpf) { setState("needs_cpf"); return; }
        const { data } = await postCashbackRead(`${API}/wallet`, {}, { ...auth(), signal: controller.signal, timeout: 45000 });
        if (controller.signal.aborted) return;
        if (data.needs_cpf) { setState("needs_cpf"); return; }
        setWallet(data.wallet);
        setState("ready");
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setState("failed");
        handlers.current.onError?.(error);
      });
    return () => controller.abort();
  }, [retry, memberId, initialData]);

  const withdraw = async () => {
    if (submitLock.current || !confirmation) return;
    submitLock.current = true;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/withdrawals`, { request_id: confirmation, confirmed: true }, auth());
      if (!mounted.current) return;
      setNotice(data.message);
      setWallet((value) => ({ ...value, withdrawal_state: data.state }));
      setRetry((value) => value + 1);
    } catch (error) {
      if (!mounted.current) return;
      const status = error?.response?.status;
      const definitive = [403, 422, 503].includes(status);
      setNotice(error?.response?.data?.detail || "Não foi possível confirmar o resultado. Atualize o saldo antes de tentar novamente ou procure o suporte.");
      setWallet((value) => ({ ...value, withdrawal_state: definitive ? "rejected" : "uncertain" }));
    } finally {
      submitLock.current = false;
      if (mounted.current) { setSubmitting(false); setConfirmation(null); }
    }
  };
  const blocked = ["pending", "uncertain", "accepted"].includes(wallet?.withdrawal_state);
  const canWithdraw = wallet?.withdrawal_enabled && Number(wallet.available_amount) > 0 && Number(wallet.in_transfer_amount) === 0 && !blocked;
  if (compact) return <section aria-label="Carteira de cashback" data-testid="cashback-wallet" className="cashback-summary">
    <h2 className="font-bold cashback-accent-text">Meu cashback</h2>
    <div className="cashback-summary-values" aria-live="polite">
      {wallet ? [["Disponível", "available_amount"], ["A receber", "receivable_amount"], ["Em transferência", "in_transfer_amount"], ["Recebido", "transferred_amount"]].map(([label, key]) => <div key={key}><span className="inline-flex items-center text-zinc-400">{label}<BalanceHelp label={label} balanceKey={key} withdrawalEnabled={wallet.withdrawal_enabled} /></span><strong>{money(wallet[key])}</strong></div>) : <p className="text-sm text-zinc-300">{({ loading: "Consultando saldo…", failed: "Saldo indisponível. Tente atualizar.", unavailable: CASHBACK_MAINTENANCE_MESSAGE, ineligible: "Exclusivo para assinantes Pro e Master ativos.", needs_cpf: "Complete seu cadastro para consultar o saldo." })[state]}</p>}
    </div>
    <div className="flex shrink-0 flex-wrap items-center gap-3">{onOrders&&<button onClick={onOrders} className="rounded-lg border border-white/15 px-3 py-2 text-xs font-semibold">Meus cupons</button>}<button type="button" aria-label="Atualizar saldo" title="Atualizar saldo" disabled={state === "loading"} onClick={() => setRetry((value) => value + 1)} className="rounded-full border border-white/15 px-3 py-2 text-sm disabled:opacity-40">↻</button>{state === "needs_cpf" ? <button onClick={() => handlers.current.onNeedsCpf?.()} className="text-sm font-semibold cashback-accent-text">Completar cadastro</button> : <a href="/meus-ingressos" className="text-sm font-semibold cashback-accent-text hover:underline">Minha Carteira →</a>}</div>
  </section>;
  return <section className="rounded-2xl border border-violet-400/20 bg-zinc-900 p-5 text-white" aria-label="Carteira de cashback" data-testid="cashback-wallet">
    <div className="flex flex-wrap items-center justify-between gap-3"><h2 className="font-nunito text-2xl font-bold">Cashback</h2><div className="flex flex-wrap gap-2"><Button disabled={state === "loading" || submitting} onClick={() => setRetry((value) => value + 1)}>Atualizar saldo</Button><Button disabled={!canWithdraw || submitting || state !== "ready"} onClick={() => { setNotice(""); setConfirmation(window.crypto.randomUUID()); }}>Solicitar resgate por Pix</Button></div></div>
    <div aria-live="polite">
      {state === "loading" && <p className="mt-4">Consultando saldo...</p>}
      {state === "failed" && <p className="mt-4" role="alert">Saldo indisponível no momento. Tente atualizar.</p>}
      {state === "unavailable" && <p className="mt-4">{CASHBACK_MAINTENANCE_MESSAGE}</p>}
      {state === "ineligible" && <p className="mt-4">Cashback disponível para assinantes Pro e Master ativos.</p>}
      {state === "needs_cpf" && <><p className="mt-4">Complete seu cadastro para consultar o cashback.</p><Button className="mt-3" onClick={() => handlers.current.onNeedsCpf?.()}>Completar cadastro</Button></>}
      {wallet && <>
        <div className="mt-5 grid grid-cols-2 gap-4">{[["Disponível para saque", "available_amount"], ["A receber", "receivable_amount"], ["Em transferência", "in_transfer_amount"], ["Já transferido", "transferred_amount"]].map(([label, key]) => <div key={key}><p className="text-sm text-zinc-400">{label}</p><p className="text-xl font-bold cashback-accent-text">{money(wallet[key])}</p></div>)}</div>
        <p className="mt-3 text-xs text-zinc-400">A receber: cashback pendente + aprovado. Este saldo é separado dos créditos de ingresso e passes de jogos.</p>
        {wallet.message && <p className="mt-3 text-sm">{wallet.message}</p>}
        <Button variant="outline" className="mt-5 border-zinc-600 bg-transparent text-zinc-100 hover:bg-zinc-800" aria-expanded={historyOpen} aria-controls={historyId} onClick={() => setHistoryOpen((open) => !open)}>{historyOpen ? "Ocultar histórico de cashback" : "Ver histórico de cashback"}</Button>
        {historyOpen && <div id={historyId}>
        <h3 className="mt-5 font-bold">Extrato de cashback</h3>
        {!wallet.records?.length ? <p className="mt-2 text-sm text-zinc-400">Nenhuma movimentação de cashback registrada.</p> : <ul className="mt-3 max-h-80 space-y-3 overflow-y-auto">
          {wallet.records.map((record) => <li key={record.id} className="rounded-xl border border-white/10 p-3">
            <div className="flex items-start justify-between gap-3"><p className="font-semibold">{record.organization || "Cashback"}</p><p className="shrink-0 font-bold cashback-accent-text">{money(record.amount)}</p></div>
            <p className="mt-1 text-sm text-zinc-300">{record.description}</p>
            <p className="mt-2 text-xs text-zinc-400">{recordDate(record.date)} · {statuses[record.status] || "Em atualização"}</p>
          </li>)}
        </ul>}
        </div>}
        {!wallet.withdrawal_enabled && <p className="mt-2 text-xs text-zinc-400">O resgate por aqui ainda não está disponível.</p>}
        {blocked && <p className="mt-3 text-sm text-amber-300">Há uma solicitação aguardando confirmação ou processamento. Atualize o saldo; se necessário, procure o suporte.</p>}
      </>}
      {notice && <p className="mt-3 text-sm" role="status">{notice}</p>}
    </div>
    <Dialog open={Boolean(confirmation)} onOpenChange={(open) => { if (!open && !submitting) setConfirmation(null); }}>
      <DialogContent className="border-white/10 bg-zinc-950 text-white">
        <DialogHeader><DialogTitle>Confirmar solicitação de resgate</DialogTitle><DialogDescription className="text-zinc-300">O resgate será solicitado para a chave Pix vinculada ao CPF do seu cadastro. A Alloyal valida o saldo, o valor mínimo e a chave Pix antes de concluir a transferência.</DialogDescription></DialogHeader>
        <p>Saldo disponível consultado: {wallet ? money(wallet.available_amount) : "—"}.</p>
        {wallet?.message && <p className="text-sm text-zinc-400">{wallet.message}</p>}
        <Button disabled={submitting} onClick={withdraw}>{submitting ? "Enviando solicitação..." : "Confirmar resgate"}</Button>
        <Button disabled={submitting} variant="outline" onClick={() => setConfirmation(null)}>Cancelar</Button>
      </DialogContent>
    </Dialog>
  </section>;
}
